import firebase from "firebase/app";
import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";

// Client reads for the content engine state the MCP writes under
// Community/{id}: ContentEngine/config, ContentConcepts, TrendReports.
// These docs hold no secrets (concept names, hooks, scrape results), so the
// admin client can read them directly; all writes stay MCP-side. Trend
// reports are schema-less at write time, so every field here is optional and
// sanitized defensively.

export type ContentEngineConfig = {
  accounts: { platform?: string; handle?: string }[];
  cadence: { postsPerWeek?: number; platforms?: string[] } | null;
  verdictThresholds: {
    winnerViews?: number;
    strongSaveRate?: number;
    dedupeDays?: number;
  } | null;
  latestTrendReportId: string | null;
  updatedAt: string | null;
};

export type ConceptStatus =
  | "planned"
  | "created"
  | "scheduled"
  | "posted"
  | "killed";

export type ConceptVerdict = "SCALE" | "ITERATE" | "KILL";

export type ContentConcept = {
  id: string;
  account: string;
  concept: string;
  series?: string;
  episode?: number;
  hook: string;
  status: ConceptStatus;
  verdict?: ConceptVerdict;
  suggestedVerdict?: ConceptVerdict;
  week?: string;
  projectId?: string;
  scheduledPostIds: string[];
  metricsFetchedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type TrendOutlier = {
  account?: string;
  followers?: number;
  title?: string;
  url?: string;
  format?: string;
  views?: number;
  saves?: number;
  saveRate?: number;
  followerRatio?: number;
};

export type TrendReport = {
  id: string;
  createdAt: string | null;
  niche?: string;
  source?: string;
  analyzedCount?: number;
  summary?: string;
  outliers: TrendOutlier[];
  accountBaselines: unknown;
  queries: string[];
  hookFormulas: string[];
  risingSounds: { label: string; url?: string }[];
};

export type AccountBaseline = {
  account: string;
  avgViews: number | null;
  topViews: number | null;
};

export type SocialLearningPattern = {
  signature: string;
  platform: string;
  mutationDimension: string;
  measurementWindowDays?: number;
  treatment: string;
  positiveEvidence: number;
  negativeEvidence: number;
  evidenceCount: number;
  status: "promising" | "proven" | "avoid";
  updatedAt: string | null;
};

export type SocialLearningObservation = {
  id: string;
  taskId: string;
  platform: string;
  primaryMetric: string;
  mutationDimension: string;
  measurementWindowDays?: number;
  candidateBrief: string;
  hypothesis: string;
  verdict: string;
  relativeLift: number;
  decidedAt: string | null;
};

export type SocialLearningMemory = {
  activeExperiment: {
    taskId?: string;
    experimentId?: string;
    platform?: string;
    primaryMetric?: string;
    mutationDimension?: string;
    measurementWindowDays?: number;
    candidateBrief?: string;
    startedAt?: string;
  } | null;
  observations: SocialLearningObservation[];
  promotedPatterns: SocialLearningPattern[];
  avoidPatterns: SocialLearningPattern[];
  latestDecision: SocialLearningObservation | null;
  updatedAt: string | null;
};

const communityDoc = (communityId: string) =>
  firestore.collection(DB_PATH.COMMUNITY).doc(communityId);

const isoOrNull = (v: any): string | null => {
  if (typeof v === "string" && v) return v;
  if (v && typeof v.toDate === "function") return v.toDate().toISOString();
  return null;
};

const num = (v: any): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

const str = (v: any): string | undefined =>
  typeof v === "string" && v ? v : undefined;

export const getContentEngineConfig = async (
  communityId: string
): Promise<ContentEngineConfig | null> => {
  const snap = await communityDoc(communityId)
    .collection("ContentEngine")
    .doc("config")
    .get();
  if (!snap.exists) return null;
  const data: any = snap.data() || {};
  return {
    accounts: Array.isArray(data.accounts)
      ? data.accounts.filter((a: any) => a && typeof a === "object")
      : [],
    cadence:
      data.cadence && typeof data.cadence === "object" ? data.cadence : null,
    verdictThresholds:
      data.verdictThresholds && typeof data.verdictThresholds === "object"
        ? data.verdictThresholds
        : null,
    latestTrendReportId: str(data.latestTrendReportId) || null,
    updatedAt: isoOrNull(data.updatedAt),
  };
};

export const getSocialLearningMemory = async (
  communityId: string
): Promise<SocialLearningMemory | null> => {
  const snap = await communityDoc(communityId)
    .collection("ContentEngine")
    .doc("learning")
    .get();
  if (!snap.exists) return null;
  const data: any = snap.data() || {};
  const observation = (value: any): SocialLearningObservation => ({
    id: str(value?.id) || "",
    taskId: str(value?.taskId) || "",
    platform: str(value?.platform) || "",
    primaryMetric: str(value?.primaryMetric) || "",
    mutationDimension: str(value?.mutationDimension) || "",
    measurementWindowDays:
      num(value?.measurementWindowDays) ?? undefined,
    candidateBrief: str(value?.candidateBrief) || "",
    hypothesis: str(value?.hypothesis) || "",
    verdict: str(value?.verdict) || "",
    relativeLift: num(value?.relativeLift) || 0,
    decidedAt: isoOrNull(value?.decidedAt),
  });
  const pattern = (value: any): SocialLearningPattern => ({
    signature: str(value?.signature) || "",
    platform: str(value?.platform) || "",
    mutationDimension: str(value?.mutationDimension) || "",
    measurementWindowDays:
      num(value?.measurementWindowDays) ?? undefined,
    treatment: str(value?.treatment) || "",
    positiveEvidence: num(value?.positiveEvidence) || 0,
    negativeEvidence: num(value?.negativeEvidence) || 0,
    evidenceCount: num(value?.evidenceCount) || 0,
    status:
      value?.status === "proven" || value?.status === "avoid"
        ? value.status
        : "promising",
    updatedAt: isoOrNull(value?.updatedAt),
  });
  return {
    activeExperiment:
      data.activeExperiment && typeof data.activeExperiment === "object"
        ? data.activeExperiment
        : null,
    observations: Array.isArray(data.observations)
      ? data.observations.map(observation)
      : [],
    promotedPatterns: Array.isArray(data.promotedPatterns)
      ? data.promotedPatterns.map(pattern)
      : [],
    avoidPatterns: Array.isArray(data.avoidPatterns)
      ? data.avoidPatterns.map(pattern)
      : [],
    latestDecision:
      data.latestDecision && typeof data.latestDecision === "object"
        ? observation(data.latestDecision)
        : null,
    updatedAt: isoOrNull(data.updatedAt),
  };
};

const sanitizeConcept = (id: string, data: any): ContentConcept => ({
  id,
  account: str(data?.account) || "",
  concept: str(data?.concept) || "",
  series: str(data?.series),
  episode: num(data?.episode),
  hook: str(data?.hook) || "",
  status: (str(data?.status) as ConceptStatus) || "planned",
  verdict: str(data?.verdict) as ConceptVerdict | undefined,
  suggestedVerdict: str(data?.suggestedVerdict) as ConceptVerdict | undefined,
  week: str(data?.week),
  projectId: str(data?.projectId),
  scheduledPostIds: Array.isArray(data?.scheduledPostIds)
    ? data.scheduledPostIds.filter((s: any) => typeof s === "string")
    : [],
  metricsFetchedAt: isoOrNull(data?.metrics?.fetchedAt),
  createdAt: isoOrNull(data?.createdAt),
  updatedAt: isoOrNull(data?.updatedAt),
});

export const getRecentConcepts = async (
  communityId: string,
  limit = 100
): Promise<ContentConcept[]> => {
  const col = communityDoc(communityId).collection("ContentConcepts");
  try {
    const snap = await col.orderBy("createdAt", "desc").limit(limit).get();
    return snap.docs.map((d) => sanitizeConcept(d.id, d.data()));
  } catch {
    const snap = await col.limit(limit).get();
    return snap.docs
      .map((d) => sanitizeConcept(d.id, d.data()))
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }
};

const sanitizeOutlier = (raw: any): TrendOutlier | null => {
  if (!raw || typeof raw !== "object") return null;
  const o: TrendOutlier = {
    account: str(raw.account),
    followers: num(raw.followers),
    title: str(raw.title) || str(raw.hook) || str(raw.caption),
    url: str(raw.url) || str(raw.link),
    format: str(raw.format),
    views: num(raw.views) ?? num(raw.plays),
    saves: num(raw.saves),
    saveRate: num(raw.saveRate),
    followerRatio: num(raw.followerRatio),
  };
  return o.title || o.account || o.url ? o : null;
};

// Free-form fields written by different scrape runs: entries can be plain
// strings or objects with a few plausible key names. Pull out a label (and a
// url for sounds) or drop the entry.
const strList = (raw: any, keys: string[]): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (entry && typeof entry === "object") {
        for (const k of keys) {
          if (typeof entry[k] === "string" && entry[k]) return entry[k];
        }
      }
      return "";
    })
    .filter(Boolean);
};

const soundList = (raw: any): { label: string; url?: string }[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === "string") return { label: entry };
      if (entry && typeof entry === "object") {
        const label =
          str(entry.title) || str(entry.name) || str(entry.sound) || str(entry.label);
        if (!label) return null;
        return { label, url: str(entry.url) || str(entry.link) };
      }
      return null;
    })
    .filter(Boolean) as { label: string; url?: string }[];
};

const sanitizeReport = (id: string, data: any): TrendReport => ({
  id,
  createdAt: isoOrNull(data?.createdAt),
  niche: str(data?.niche),
  source: str(data?.source),
  analyzedCount: num(data?.analyzedCount),
  summary: str(data?.summary),
  outliers: Array.isArray(data?.outliers)
    ? (data.outliers.map(sanitizeOutlier).filter(Boolean) as TrendOutlier[])
    : [],
  accountBaselines: data?.accountBaselines,
  queries: strList(data?.queries, ["query", "phrase", "term"]),
  hookFormulas: strList(data?.hookFormulas, ["formula", "hook", "pattern", "name"]),
  risingSounds: soundList(data?.risingSounds),
});

// Doc ids are yyyy-mm-dd, so lexical desc on document id is newest first.
// A few recent reports (not just one) so scrapes from different platforms
// all surface with their source labeled.
export const getRecentTrendReports = async (
  communityId: string,
  limit = 3
): Promise<TrendReport[]> => {
  const snap = await communityDoc(communityId)
    .collection("TrendReports")
    .orderBy(firebase.firestore.FieldPath.documentId(), "desc")
    .limit(limit)
    .get();
  return snap.docs.map((d) => sanitizeReport(d.id, d.data()));
};

// accountBaselines has no fixed shape (map of account -> number, map of
// account -> object, or an array of rows). Normalize whatever is there and
// drop entries with no usable numbers.
export const parseAccountBaselines = (raw: unknown): AccountBaseline[] => {
  const rows: AccountBaseline[] = [];
  const pushRow = (account: unknown, value: unknown) => {
    if (typeof account !== "string" || !account) return;
    let avgViews: number | null = null;
    let topViews: number | null = null;
    if (typeof value === "number" && Number.isFinite(value)) {
      avgViews = value;
    } else if (value && typeof value === "object") {
      const v: any = value;
      avgViews =
        num(v.avgViews) ?? num(v.avg) ?? num(v.averageViews) ?? num(v.medianViews) ?? null;
      topViews = num(v.topViews) ?? num(v.maxViews) ?? num(v.top) ?? null;
    }
    if (avgViews !== null || topViews !== null) {
      rows.push({ account: account.replace(/^@/, ""), avgViews, topViews });
    }
  };
  if (Array.isArray(raw)) {
    raw.forEach((r: any) => pushRow(r?.account || r?.handle, r));
  } else if (raw && typeof raw === "object") {
    Object.entries(raw as Record<string, unknown>).forEach(([k, v]) =>
      pushRow(k, v)
    );
  }
  return rows;
};
